import classnames from 'classnames';
import type { ReactNode } from 'react';

export enum FormLabelVariant {
  /**
   * You should use this one 99.9% of the time.
   */
  Default = 'default',
  /**
   * This is used when an input label exists within a FilterPanel.
   */
  Filter = 'filter',
}

type FormLabelProps = {
  /**
   * Text (or JSX nodes) to be rendered as the label.
   */
  label: ReactNode;
  /**
   * The input component being labeled.
   */
  children?: ReactNode;
  /**
   * The id of the form item this is labeling (if no input was passed as children).
   */
  htmlFor?: string;
  /**
   * If the label will appear inline (instead of the default block styling).
   */
  isInline?: boolean;
  /**
   * What the "required text" should say. Could also be a component.
   */
  requiredText?: ReactNode;
  /**
   * Which type of form label to use.
   */
  variant?: FormLabelVariant;
};

/**
 * A standalone <label> component, for labeling form components.
 */
const FormLabel = ({
  label,
  children = null,
  htmlFor,
  isInline,
  requiredText,
  variant = FormLabelVariant.Default,
}: FormLabelProps) => (
  <label
    htmlFor={htmlFor}
    className={classnames('fossa-form__label', `fossa-form__label--${variant}`, {
      'fossa-form__label--inline': isInline,
    })}
  >
    <div className="fossa-form__label__text">
      {label} {requiredText && (
        <span className="fossa-form__label__text__required">
          {requiredText}
        </span>
      )}
    </div>
    {children}
  </label>
);

export default FormLabel;
